import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useConfigurationStore, useDeviceStore } from '@/stores'
import { useKeySDK } from '../keys/composables/useKeySDK'
import { parseMapToKeySettings } from '../keys/utils/keyMapSetting'
import type { KeySetting } from '../keys/types'
import { createCoalesceLatestRequestQueue } from '@/utils/coalesceLatestRequest'

/**
 * 获取当前鼠标按键的改键映射结果（keySettings）
 *
 * 用于在改键页面之外（如 DPI 页面）感知每个物理按键经过改键后的实际功能，
 * 只读取 返回的 keySettings 仅包含被修改过的按键
 */
export function useMouseKeyMappings() {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const configStore = useConfigurationStore()
  const { currentConfig } = storeToRefs(configStore)

  const { initKeys } = useKeySDK()

  const layout = computed(() => currentDevice.value?.manifest.layout)
  const keySettings = ref<KeySetting[]>([])

  /** 板载切换上报后的按键回读：最多 1 in-flight + 1 pending */
  const cfgKeyFetchQueue = createCoalesceLatestRequestQueue('mouseKeys.cfgReport')

  const loadKeySettings = () => {
    cfgKeyFetchQueue.enqueue(async () => {
      const keyMap = await initKeys()
      keySettings.value = parseMapToKeySettings({ keyMap, layoutKeys: layout.value?.keys })
    })
  }

  onMounted(loadKeySettings)
  watch(() => currentConfig.value?.index, loadKeySettings)
  watch(currentDevice, loadKeySettings)

  return { keySettings, loadKeySettings }
}
